import * as React from 'react';

import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import cn from '../utils/cn';

const buttonVariants = cva(
  'inline-flex items-center text-white justify-center gap-2 whitespace-nowrap !rounded-[4px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        outlined:
          'border border-input bg-white text-[#666] shadow-sm hover:bg-accent hover:text-accent-foreground',
        outlined_red:
          'border border-primary-red bg-white text-primary-red shadow-sm hover:bg-accent hover:text-accent-foreground',
        outlined_blue:
          'border border-primary-bright-blue bg-white text-primary-bright-blue shadow-sm hover:bg-accent hover:text-accent-foreground',
        contained_blue: 'bg-primary-bright-blue',
        contained_red: 'bg-primary-red',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        text: '!bg-none text-[#666] !px-0',
        link_blue:
          'text-primary-bright-blue underline underline-offset-4 !px-0',
        link_red: 'text-primary-red underline underline-offset-4 !px-0',
        link_disabled: 'text-[#999] underline underline-offset-4 !px-0',
      },
      size: {
        default: 'h-8 px-4 py-2',
        sm: 'h-7 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonProps = {
  asChild?: boolean;
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading = false,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant: disabled ? 'outlined' : variant,
            size,
            className,
          })
        )}
        type={type}
        disabled={loading || disabled}
        ref={ref}
        {...props}
      >
        <Slottable>{children}</Slottable>
        {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { buttonVariants };

export default Button;
